<template>
  <form @submit.prevent :novalidate="true">
    <b-card>
      <template v-slot:header>
        <!-- Client Selection -->
        <b-row>
          <b-form-group class="col-lg-6">
            <CustomTreeSelect
              v-model="locationValue"
              :clearable="false"
              :placeholder="$t('Webportal.LocationFilter.Placeholder')"
              :options="locationItems"
              :disabled="isBusy || isLoading"
              @input="clientLocationSelected">
            </CustomTreeSelect>
          </b-form-group>
        </b-row>
      </template>

      <div v-bind:class="{ spinner: isLoading }">
        <!-- Google Preselection -->
        <div>
          <b-row class="mb-3">
            <b-col cols="3">
              <label class="col-lg-4 col-md-6 col-form-label pt-0">
                <b-card-img
                  style="width: 110px"
                  fluid
                  src="/static/img/maps-circle.png"
                ></b-card-img>
              </label>
            </b-col>
            <b-col cols="7">
              <treeselect
                v-validate
                data-vv-name="GoogleCompetitors"
                v-model="googlePreselection"
                :clearable="false"
                :multiple="true"
                :placeholder="$t('Webportal.CX.CompetitorFilter.Placeholder')"
                :options="googlePreselectionList"
                :disableFuzzyMatching="true"
                :disabled="disableSelection"
                @select="selectChangeGoogle"
              ></treeselect>
            </b-col>
          </b-row>
        </div>
        <hr />

        <!-- Insert all api options -->
        <div v-for="apiName in allApiNamesLC" v-bind:key="`${apiName}Container`" :class="`ml-3 comp-row ${apiName}-row`">
          <b-row class="mb-3">
            <b-col cols="3">
              <label class="col-lg-4 col-md-6 col-form-label pt-0">
                <b-card-img
                  style="width: 110px"
                  fluid
                  :src="`/static/img/${apiName}-logo.png`"
                  :class="`${apiName}-logo`"
                ></b-card-img>
              </label>
            </b-col>
            <b-col cols="7">
              <treeselect
                v-validate
                :data-vv-name="`${apiName}Competitors`"
                :clearable="false"
                :placeholder="$t('Webportal.CX.CompetitorFilter.Placeholder')"
                :multiple="true"
                v-model="siht[`${apiName}Competitors`]"
                :options="siht[`${apiName}CompetitorList`]"
                :disableFuzzyMatching="true"
                :disabled="disableSelection"
              ></treeselect>
            </b-col>
          </b-row>
        </div>

        <hr />
      </div>

      <template v-slot:footer>
        <div class="float-end">
          <button
            type="button"
            @click="cancel"
            class="btn btn-secondary"
            :disabled="!isFormChanged || isBusy"
          >
            <font-awesome-icon :icon="['far', 'ban']" />
            {{ $t("Webportal.Cancel") }}
          </button>
          <button
            type="button"
            @click="submit"
            class="btn btn-primary"
            :disabled="
              !isFormChanged || !isFormValid || isBusy /* || disableEditing */
            "
          >
            <font-awesome-icon :icon="['far', 'dot-circle']" />
            {{ $t("Webportal.Save") }}
          </button>
        </div>
      </template>
    </b-card>
  </form>
</template>

<script>
import axios from "axios";

import CustomTreeSelect from '@/components/Shared/CustomTreeSelect';
import AppConfig from "@/mixins/AppConfig-APP_TARGET";
import AdministrationService from "@/services/AdministrationService";
import DashboardService from "@/services/DashboardService";

import AuthenticationService from "@/services/AuthenticationService";

import useVuelidate from '@vuelidate/core';
import { required, email, helpers } from '@vuelidate/validators';

export default {
  name: "CXCompetitorSettings",

  mixins: [AppConfig],

  components: { CustomTreeSelect },

  setup () {
    return { v$: useVuelidate() }
  },

  data() {
    return {
      allApiNames:   ["Google", "Seobility", "Autoscout", "Mobile", "Autocasion", "Marktplaats"],
      allApiNamesLC: ["google", "seobility", "autoscout", "mobile", "autocasion", "marktplaats"],

      googlePreselection: null,
      googleCompetitors: null,
      autoscoutCompetitors: null,
      seobilityCompetitors: null,
      mobileCompetitors: null,
      autocasionCompetitors: null,
      marktplaatsCompetitors: null,

      googlePreselectionList: [],
      googleCompetitorList: [],
      autoscoutCompetitorList: [],
      seobilityCompetitorList: [],
      mobileCompetitorList: [],
      autocasionCompetitorList: [],
      marktplaatsCompetitorList: [],

      apiId_Google: 1,
      apiId_Seobility: 2,
      apiId_Autoscout: 3,
      apiId_Mobile: 4,
      apiId_Autocasion: 7,
      apiId_Marktplaats: 8,

      locationItems: [],
      locationValue: null,

      // Needed to reduce request to google
      blockGoogle: false,

      isBusy: false,
      isLoading: false,
      noDataAlert: 0
    };
  },

  watch: {},

  computed: {
    siht() {
      // Used as wrapper to access this in html context
      return this;
    },
    disableSelection() {
      return this.isBusy || this.locationValue == null || this.blockGoogle;
    },
    isFormChanged() {
      return this.v$.$anyDirty;
    },
    isFormValid() {
      return !this.v$.$error || this.v$.$anyDirty;
    }
  },

  activated() {
    this.getClientLocations();
  },

  beforeRouteLeave(to, from, next) {
    this.$emit("unsavedChanges", this.isFormChanged, next);
  },

  methods: {
    getClientLocations(withSelectionClear = false) {
      this.isLoading = true;
      const optionsCancelToken = this.initCancelToken(this.optionsCancelToken);
      let currentClient = AuthenticationService.localUserProfile.ClientId
        ? AuthenticationService.localUserProfile.ClientId
        : null;

      DashboardService.getOptions(
        { client: currentClient } || null,
        optionsCancelToken
      )
      .then(response => {
        if (response.data) {
          this.isLoading = false;

          if (response.data.locations) {
            this.locationItems = response.data.locations;
            // this.locationValue = _.first(this.locationItems);
          }
        }
      })
      .catch(error => {
        this.isLoading = false;
        console.warn("Error while receive dashboard filter values: " + error);
      });
    },

    // Called whenever a client location was selected/ has changed
    clientLocationSelected(value) {
      if (this.clientValue === value) return;

      let currentValue = this.clientValue;
      this.clientValue = value;

      this.googleCompetitorList = [];
      this.seobilityCompetitorList = [];
      this.mobileCompetitorList = [];
      this.autoscoutCompetitorList = [];
      this.autocasionCompetitorList = [];
      this.marktplaatsCompetitorList = [];

      this.googleCompetitors = null;
      this.seobilityCompetitors = null;
      this.mobileCompetitors = null;
      this.autoscoutCompetitors = null;
      this.autocasionCompetitors = null;
      this.marktplaatsCompetitors = null;

      this.$forceUpdate();

      this.isLoading = true;

      let allStoredNodes = [];

      // ARRRGGGH: AdministrationService? AdministrationService! -.-'
      AdministrationService.getCompetitorSettings(value)
        .then(response => {
          this.iterateOverAllApis((apiName, apiId, apiCompetitorIds, apiCompetitorNodeList) => {
            let api = response?.data?.[apiName];
            if (!api) return;

            this[`apiId_${apiName}`] = api.ApiID;

            let comps = api.Competitors?.map(o => ({
              label: o.Name,
              id: o.CompetitorID,
              GoogleID: o.GoogleID,
              isGoogleId: false
            }));

            if (!comps) return;

            let lcApiName = apiName.toLowerCase();
            this[`${lcApiName}Competitors`] = comps.map(c => c.id);
            this[`${lcApiName}CompetitorList`] = comps;

            allStoredNodes.push(...comps);
          }, false);

          this.v$.$reset();
        })
        .then(() => AdministrationService.getNearbyLocation(value))
        .then(response => {
            // Replace preselection nodes
            this.googlePreselectionList = response.data.locations.map(l =>
              ({
                label: l.name,
                id: l.googleId,
                GoogleID: l.googleId,
                isGoogleId: true
              })
            );
        })
        .then(() => {
            // Select all preselections for stored competitors
            this.googlePreselection = _.map(_.intersectionBy(this.googlePreselectionList, allStoredNodes, "GoogleID"), "id");

            // Fill selections with stored nodes, but preserve already existing nodes
            this.iterateOverAllApis((apiName, apiId, apiCompetitorIds, apiCompetitorNodeList) => {
              let list = apiCompetitorNodeList;
              list = _(list)
                      .concat(
                        _.cloneDeep(allStoredNodes)
                      )
                      .uniqBy("id")
                      .value();

              this[`${apiName.toLowerCase()}CompetitorList`] = list;
            });
        })
        .catch(error => console.error(error))
        .finally(() => (this.isLoading = false));

      // this.$emit("unsavedChanges", this.isFormChanged, (result) => {
      //   if (result === false) {
      //     this.clientValue = currentValue;
      //   } else this.getClientSettings();
      // });
    },

    selectChangeGoogle(node) {
      // Crawl node only on select
      if (node?.id && node?.isGoogleId)
        this.crawlGoogleID(node);
    },

    // Competitor for google choosed. Need to check if there are still data for the others or crawling is needed.
    crawlGoogleID(node) {
      this.isLoading = true;
      this.blockGoogle = true;

      AdministrationService.getCompetitorLinks(this.locationValue, node.id)
        .then(response => {
          // Google is always available, as request object is from Google
          this.googleCompetitorList.push(node);

          let urls = response?.data;
          if (!urls) return;

          if (urls.CompetitorID) {
            node.id = urls.CompetitorID;
          }

          this.iterateOverAllApis((apiName, apiId, apiCompetitorIds, apiCompetitorNodeList) => {
            let urlPropName = ((apiName == "Seobility") ? "" : apiName) + "URL";
            if (urls[urlPropName]) {
              node[urlPropName] = urls[urlPropName];
              apiCompetitorNodeList.push(node);
            }
          }, false);
        })
        .catch(error =>
          console.error("Error while crawling competitor: " + error)
        )
        .finally(() => {
          this.isLoading = false;
          this.blockGoogle = false;
        });
    },

    initCancelToken(token) {
      // Cancel previous request
      if (token) token.cancel();
      return axios.CancelToken.source();
    },

    getCurrentSettings() {
      let currentSettings = [];

      this.iterateOverAllApis((apiName, apiId, apiCompetitorIds, apiCompetitorNodeList) => {
        currentSettings.push(...apiCompetitorNodeList
          .filter(competitor => apiCompetitorIds.indexOf(competitor.id) >= 0)
          .map(competitor =>
            ({
              competitorId: competitor.id,
              apiId: apiId,
              isGoogleId: competitor.isGoogleId,

              // TODO: HACK
              URL: competitor.URL,
              MobileURL: competitor.MobileURL,
              AutoscoutURL: competitor.AutoscoutURL
            })
          )
        );
      });

      return currentSettings;
    },

    // callback: fn(apiName, apiId, apiCompetitorIds, apiCompetitorNodeList)
    iterateOverAllApis(callback, checkNil = true) {
      this.allApiNames.forEach(apiName => {
        let apiId = this[`apiId_${apiName}`];

        let lcApiName = apiName.toLowerCase();
        let apiCompetitorsIds = this[`${lcApiName}Competitors`];
        let apiCompetitorList = this[`${lcApiName}CompetitorList`];

        if (checkNil && (!apiId || _.isNil(apiCompetitorsIds) || _.isNil(apiCompetitorList)))
          return;

        callback(apiName, apiId, apiCompetitorsIds, apiCompetitorList);
      });
    },

    cancel() {
      this.$emit("unsavedChanges", this.isFormChanged, result => {
        if (result !== false) {
          this.getCurrentSettings();
        }
      });
    },

    submit() {
      this.isBusy = true;
      let currentSettings = this.getCurrentSettings();

      AdministrationService
        .setCompetitorSettings({
          locationId: this.locationValue,
          competitors: currentSettings
        })
        .then(response => {
          // The response contains the related competitorIds for all stored googleIds
          let idMap = response?.data;
          if (!idMap) return;

          let googleIdsToCheck = currentSettings.map(c => c.competitorId);

          // Overwrite google-ids with competitor-ids
          this.iterateOverAllApis(
            (apiName, apiId, apiCompetitorIds, apiCompetitorNodeList) => {
              apiCompetitorIds.forEach((id, index) => {
                // Overwrite all matches, as successful. No checks needed.
                let compId = idMap[id];
                if (compId) apiCompetitorIds[index] = compId;
              });

              apiCompetitorNodeList.forEach((c, index) => {
                // Skip competitor ids and non selected elements
                if (!c.isGoogleId || googleIdsToCheck.indexOf(c.id) < 0) return;

                let compId = idMap[c.id];
                if (!compId) {
                  console.error(
                    `The database has not stored selected google competitor '${c.label}' (${c.id}).`
                  );
                  return;
                }

                c.id = compId;
                c.isGoogleId = false;
              });
            }
          );

          this.v$.$reset();
        })
        .catch(error => {
          this.$emit("saveError", result => {});
        })
        .finally(() => {
          this.isBusy = false;
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.group-title {
  font-size: 1.3rem;
}
.comp-row {
  min-height: 65px;
}
.autoscout-row {
  margin-top: -15px;
  margin-bottom: 10px;
}
</style>
